import type { NavSet, TocSection } from '@squareup/dex-types-shared-docs';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';

// Bundled manifest — safe on client (just JSON).
import manifestJson from '../mdx/manifest.json';

import { isVariantPayloadKey } from './mdx-manifest';
import type { Manifest, QueryLike } from './mdx-manifest';

interface MdxMetadata {
  title: string;
  slug: string;
  browserTitle?: string;
  summary?: string;
  metaDescription?: string;
  summaryPlacement?: 'body-after-applies-to';
  releaseStatus?: string;
  hideToc?: boolean;
  pageType?: 'landing';
  tocSections?: Array<TocSection>;
  leftNavId?: string;
}

interface MdxPayload {
  mdxSource: MDXRemoteSerializeResult;
  metadata: MdxMetadata;
  navSet: NavSet;
}

const clientManifest = manifestJson as unknown as Manifest;

// Hide the server-only `require('fs')` from webpack's static analyzer so the
// client bundle doesn't try to resolve Node built-ins. `eval('require')` uses
// the ambient CommonJS `require` at runtime and is opaque to webpack. Guarded
// by a `typeof window` check so the inner code never runs in the browser.
type ServerRequire = (id: string) => unknown;
const nodeRequire: ServerRequire | null =
  typeof window === 'undefined'
    ? // eslint-disable-next-line no-eval -- deliberate webpack-opaque server-only require
      (eval('require') as ServerRequire)
    : null;

interface MdxPayloadFetchOptions {
  publicSlug?: string;
  query?: QueryLike;
}

function appendQueryValue(
  params: URLSearchParams,
  key: string,
  value: string | string[] | undefined
): void {
  if (
    value === undefined ||
    key === 'slug' ||
    key === 'payloadKey' ||
    key === 'publicSlug'
  ) {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      params.append(key, item);
    }
    return;
  }
  params.append(key, value);
}

function resolveGeneratedDocPath(
  path: typeof import('path'),
  baseDir: string,
  key: string,
  extension: string
): string | null {
  const resolvedPath = path.resolve(baseDir, `${key}.${extension}`);

  if (!resolvedPath.startsWith(baseDir + path.sep)) {
    return null;
  }

  return resolvedPath;
}

/**
 * Server-side only: reads a precompiled MDX payload from disk.
 * Returns null if the payload is missing (manifest/build skew → fall through).
 *
 * Kept async for API symmetry with fetchMdxPayloadFromCdn so the call site
 * can use a single `await` regardless of which side it's on.
 */
// eslint-disable-next-line require-await -- async for call-site symmetry
async function loadMdxPayloadFromDisk(
  payloadKey: string
): Promise<MdxPayload | null> {
  if (!nodeRequire) {
    return null;
  }
  const fs = nodeRequire('fs') as typeof import('fs');
  const path = nodeRequire('path') as typeof import('path');

  const payloadDir = isVariantPayloadKey(payloadKey)
    ? path.resolve(process.cwd(), '.generated/docs/_mdx-payloads')
    : path.resolve(process.cwd(), 'public/docs/_mdx-payloads');
  const payloadPath = resolveGeneratedDocPath(
    path,
    payloadDir,
    payloadKey,
    'json'
  );

  // Defense-in-depth traversal guard. Slugs come from a trusted manifest,
  // but verify the resolved path still lives under the generated payload dir.
  if (!payloadPath) {
    return null;
  }

  try {
    // eslint-disable-next-line unicorn/prefer-json-parse-buffer -- JSON.parse types require string in our tsconfig
    const raw = fs.readFileSync(payloadPath, 'utf8');
    return JSON.parse(raw) as MdxPayload;
  } catch {
    return null;
  }
}

/**
 * Client-side: fetches the precompiled MDX payload JSON from the CDN.
 * Returns null on any failure (network error, 404) so the caller can
 * fall through to Contentful.
 */
function getMdxPayloadCdnUrl(
  payloadKey: string,
  options: MdxPayloadFetchOptions = {}
): string {
  if (isVariantPayloadKey(payloadKey)) {
    const params = new URLSearchParams(
      options.publicSlug ? { publicSlug: options.publicSlug } : {}
    );
    for (const [key, value] of Object.entries(options.query || {})) {
      appendQueryValue(params, key, value);
    }

    const query = params.toString();
    const encodedPayloadKey = payloadKey
      .split('/')
      .map(encodeURIComponent)
      .join('/');

    return `/docs/_mdx-payloads/${encodedPayloadKey}.json${
      query ? `?${query}` : ''
    }`;
  }

  const buildId = process.env.NEXT_PUBLIC_APP_BUILD_ID;
  const buildQuery = buildId
    ? `?${new URLSearchParams({ v: buildId }).toString()}`
    : '';

  return `/docs/_mdx-payloads/${payloadKey}.json${buildQuery}`;
}

async function fetchMdxPayloadFromCdn(
  payloadKey: string,
  options: MdxPayloadFetchOptions = {}
): Promise<MdxPayload | null> {
  try {
    const res = await fetch(getMdxPayloadCdnUrl(payloadKey, options));
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as MdxPayload;
  } catch {
    return null;
  }
}

// eslint-disable-next-line require-await -- async for call-site symmetry
async function loadMdxMarkdownFromDisk(
  markdownKey: string
): Promise<string | null> {
  if (!nodeRequire) {
    return null;
  }
  const fs = nodeRequire('fs') as typeof import('fs');
  const path = nodeRequire('path') as typeof import('path');

  const markdownDir = isVariantPayloadKey(markdownKey)
    ? path.resolve(process.cwd(), '.generated/docs/_mdx-markdown')
    : path.resolve(process.cwd(), 'public/docs/_mdx-markdown');
  const markdownPath = resolveGeneratedDocPath(
    path,
    markdownDir,
    markdownKey,
    'md'
  );

  if (!markdownPath) {
    return null;
  }

  try {
    return fs.readFileSync(markdownPath, 'utf8');
  } catch {
    return null;
  }
}

/**
 * Server-side returns the on-disk manifest (re-reads once per process);
 * on the client returns the bundled JSON imported above.
 *
 * Kept as an async function so callers don't have to care which side they
 * are on — just `await loadManifest()`.
 */
let cachedServerManifest: Manifest | null = null;
// eslint-disable-next-line require-await -- async for call-site symmetry
async function loadManifest(): Promise<Manifest> {
  if (!nodeRequire) {
    return clientManifest;
  }
  if (cachedServerManifest) {
    return cachedServerManifest;
  }
  const fs = nodeRequire('fs') as typeof import('fs');
  const path = nodeRequire('path') as typeof import('path');
  const manifestPath = path.resolve(process.cwd(), 'mdx/manifest.json');
  try {
    // eslint-disable-next-line unicorn/prefer-json-parse-buffer -- JSON.parse types require string in our tsconfig
    const raw = fs.readFileSync(manifestPath, 'utf8');
    cachedServerManifest = JSON.parse(raw) as Manifest;
  } catch {
    cachedServerManifest = clientManifest;
  }
  return cachedServerManifest;
}

export {
  loadMdxPayloadFromDisk,
  loadMdxMarkdownFromDisk,
  fetchMdxPayloadFromCdn,
  getMdxPayloadCdnUrl,
  loadManifest,
  clientManifest,
  nodeRequire,
};
export type { Manifest } from './mdx-manifest';
export type { MdxPayload, MdxMetadata, ServerRequire };
